
import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * JOVI Free Card Holder Configuration
 *
 * IMPORTANT:
 * JOVI_FREE_GIFT_VARIANT_ID mesti menggunakan VARIANT ID,
 * bukan Product ID.
 *
 * RM200 = 20000 cents dalam Shopify.
 */
const JOVI_FREE_GIFT_VARIANT_ID = 9458477269097;
const JOVI_FREE_GIFT_MINIMUM_SPEND = 20000;

/**
 * Sync JOVI free gift with the cart.
 *
 * - Adds one free gift when qualifying spend reaches RM200.
 * - Removes the gift when spend falls below RM200.
 * - Prevents duplicate gift quantities.
 * - Excludes the gift itself from qualifying spend.
 */
let joviGiftSyncRunning = false;

async function syncJoviFreeGift() {
  if (joviGiftSyncRunning) return;

  joviGiftSyncRunning = true;

  try {
    const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!cartResponse.ok) {
      throw new Error('Unable to retrieve Shopify cart.');
    }

    const cart = await cartResponse.json();

    const giftLines = cart.items.filter(
      (item) => Number(item.variant_id) === Number(JOVI_FREE_GIFT_VARIANT_ID)
    );

    const qualifyingSpend = cart.items.reduce((total, item) => {
      if (Number(item.variant_id) === Number(JOVI_FREE_GIFT_VARIANT_ID)) {
        return total;
      }

      return total + Number(item.final_line_price || 0);
    }, 0);

    const qualifies = qualifyingSpend >= JOVI_FREE_GIFT_MINIMUM_SPEND;

    // Customer qualifies: keep exactly one gift.
    if (qualifies) {
      if (giftLines.length === 0) {
        const addResponse = await fetch(
          `${window.Shopify.routes.root}cart/add.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items: [
                {
                  id: JOVI_FREE_GIFT_VARIANT_ID,
                  quantity: 1,
                },
              ],
            }),
          }
        );

        if (!addResponse.ok) {
          throw new Error('Unable to add JOVI free gift.');
        }

        // Reload cart data after adding the gift.
        await fetch(`${window.Shopify.routes.root}cart.js`, {
          headers: { Accept: 'application/json' },
        });
      } else if (giftLines.length > 1 || giftLines[0].quantity > 1) {
        // Keep one gift and remove any duplicates.
        const updates = {};

        giftLines.forEach((item) => {
          updates[item.key] =
            item.key === giftLines[0].key ? 1 : 0;
        });

        const updateResponse = await fetch(
          `${window.Shopify.routes.root}cart/update.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ updates }),
          }
        );

        if (!updateResponse.ok) {
          throw new Error('Unable to update JOVI free gift.');
        }
      }

      return;
    }

    // Customer does not qualify: remove the gift.
    if (giftLines.length > 0) {
      const updates = {};

      giftLines.forEach((item) => {
        updates[item.key] = 0;
      });

      const removeResponse = await fetch(
        `${window.Shopify.routes.root}cart/update.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ updates }),
        }
      );

      if (!removeResponse.ok) {
        throw new Error('Unable to remove JOVI free gift.');
      }
    }
  } catch (error) {
    console.error('[JOVI Free Gift]', error);
  } finally {
    joviGiftSyncRunning = false;
  }
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} [liveRegion]
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    this.addEventListener(
      DialogOpenEvent.eventName,
      this.#updateStickyState
    );

    this.addEventListener(
      DialogOpenEvent.eventName,
      this.#handleHistoryOpen
    );

    this.addEventListener(
      DialogCloseEvent.eventName,
      this.#handleHistoryClose
    );

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#updateStickyState
    );

    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#handleHistoryOpen
    );

    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleHistoryClose
    );

    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();

    window.addEventListener(
      'popstate',
      this.#handlePopState,
      { signal: this.#historyAbortController.signal }
    );
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();

    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty(
        '--dialog-drawer-closing-animation',
        'none'
      );

      await this.closeDialog();

      this.refs.dialog.style.removeProperty(
        '--dialog-drawer-closing-animation'
      );
    }
  };

  /**
   * Handles cart add events.
   *
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);

    // Check whether the cart qualifies for the JOVI free gift.
    syncJoviFreeGift();
  };

  /**
   * Check gift eligibility when the cart drawer opens.
   */
  #handleDrawerOpen = () => {
    syncJoviFreeGift();
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   *
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion =
      /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);

    if (
      !this.refs.dialog?.open ||
      !liveRegion ||
      cartCount === undefined
    ) {
      return;
    }

    liveRegion.textContent =
      `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    // Check free gift eligibility when opening the drawer.
    syncJoviFreeGift();

    /**
     * Close cart drawer when installments CTA is clicked
     * to avoid overlapping dialogs.
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent =
        document.querySelector('shopify-payment-terms')?.shadowRoot;

      const cta = installmentsContent?.querySelector(
        '#shopify-installments-cta'
      );

      cta?.addEventListener('click', this.closeDialog, {
        once: true,
      });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);

    if (!dialog) return;

    // Refs do not cross nested component boundaries.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold ? 'false' : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}
